import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, FileCheck, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Governance() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const ticketsRes = await axios.get(`${API}/tickets`);
      setTickets(ticketsRes.data);

      // Fetch audit logs for all tickets
      const auditPromises = ticketsRes.data.map(async (ticket) => {
        try {
          const auditRes = await axios.get(`${API}/audit/${ticket.id}`);
          return auditRes.data.map(log => ({ ...log, ticket }));
        } catch (err) {
          return [];
        }
      });

      const auditResults = await Promise.all(auditPromises);
      const allAudits = auditResults.flat().sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setAuditLogs(allAudits);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  const completedTickets = tickets.filter(t => t.status === 'completed').length;
  const activeTickets = tickets.filter(t => !['completed', 'rejected'].includes(t.status)).length;
  const complianceLogs = auditLogs.filter(log => log.compliance_status);

  return (
    <div className="space-y-6" data-testid="governance-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Governance & Compliance</h1>
        <p className="text-muted-foreground mt-2">Audit logs, compliance tracking, and governance oversight</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Audit Logs</CardTitle>
            <Shield className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{auditLogs.length}</div>
            <p className="text-xs text-muted-foreground">All tracked actions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Checks</CardTitle>
            <FileCheck className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{complianceLogs.length}</div>
            <p className="text-xs text-muted-foreground">Compliance validations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tickets</CardTitle>
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeTickets}</div>
            <p className="text-xs text-muted-foreground">Pending governance review</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="audit" className="w-full">
        <TabsList>
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
          <TabsTrigger value="compliance">Compliance Tracking</TabsTrigger>
        </TabsList>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Trail</CardTitle>
              <CardDescription>Complete history of all actions across all tickets</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No audit logs yet
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-4 p-3 border rounded-lg text-sm hover:bg-accent" data-testid={`audit-log-${log.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="font-mono text-xs">
                            {log.ticket?.ticket_number || 'N/A'}
                          </Badge>
                          <span className="font-medium">{log.action.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {log.ticket?.title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(log.timestamp).toLocaleString()} • by {log.actor}
                        </div>
                      </div>
                      {log.compliance_status && (
                        <Badge 
                          variant={log.compliance_status === 'compliant' ? 'default' : 'destructive'}
                          className="text-xs"
                        >
                          {log.compliance_status.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Compliance Status</CardTitle>
                <CardDescription>Clean Core compliance and SAP best practices adherence</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-muted-foreground mb-2">Completed Tickets</div>
                      <div className="text-3xl font-bold">{completedTickets}</div>
                      <div className="text-xs text-green-600 mt-1">All passed compliance checks</div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm text-muted-foreground mb-2">Pending Reviews</div>
                      <div className="text-3xl font-bold">{activeTickets}</div>
                      <div className="text-xs text-amber-600 mt-1">Awaiting governance approval</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Compliance Validations</h3>
                    {complianceLogs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No compliance checks performed yet
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {complianceLogs.slice(0, 10).map((log) => (
                          <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`compliance-log-${log.id}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="font-mono text-xs">
                                  {log.ticket?.ticket_number}
                                </Badge>
                                <span className="font-medium text-sm">{log.action.replace(/_/g, ' ')}</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(log.timestamp).toLocaleString()}
                              </div>
                            </div>
                            <Badge 
                              variant={log.compliance_status === 'compliant' ? 'default' : 'destructive'}
                              className="text-xs"
                            >
                              {log.compliance_status === 'compliant' ? (
                                <><FileCheck className="mr-1 h-3 w-3" /> Compliant</>
                              ) : (
                                <><AlertTriangle className="mr-1 h-3 w-3" /> Non-Compliant</>
                              )}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Governance Policies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <FileCheck className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="font-medium">Clean Core Compliance</div>
                      <div className="text-muted-foreground text-xs">All customizations must follow SAP Clean Core principles</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <FileCheck className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="font-medium">Test Coverage</div>
                      <div className="text-muted-foreground text-xs">Minimum 80% test coverage required for deployment</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <FileCheck className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="font-medium">Security Standards</div>
                      <div className="text-muted-foreground text-xs">All code must pass security and vulnerability scans</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <FileCheck className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="font-medium">Audit Trail</div>
                      <div className="text-muted-foreground text-xs">All actions must be logged for compliance and traceability</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
